<template>
  <div class="gm-panel properties-panel gm-scroll">
    <h3 class="gm-title properties-title">Cell Properties</h3>

    <div v-if="!selectedCell" class="gm-empty">
      <p class="gm-empty__title">No cell selected</p>
      <p class="gm-empty__hint">Use the Select tool to inspect cells</p>
    </div>

    <div v-else class="properties-content">
      <!-- Cell Info -->
      <div class="property-section">
        <h4 class="gm-section-title">Cell Info</h4>
        <div class="property-row">
          <span class="property-label">Position:</span>
          <span class="property-value">{{ selectedCell.x }}, {{ selectedCell.y }}</span>
        </div>
        <div class="property-row">
          <span class="property-label">Layer:</span>
          <span class="property-value">{{ selectedCell.layer }} ({{ layerName }})</span>
        </div>
        <div class="property-row">
          <span class="property-label">Terrain:</span>
          <select
            v-model="cellTerrain"
            class="gm-select terrain-select"
            @change="updateCellTerrain"
          >
            <option value="grass">Grass (1.0x)</option>
            <option value="road">Road (0.75x)</option>
            <option value="dirt">Dirt (1.0x)</option>
            <option value="sand">Sand (1.25x)</option>
            <option value="swamp">Swamp (2.0x)</option>
            <option value="water">Water (∞)</option>
            <option value="lava">Lava (∞)</option>
            <option value="snow">Snow (1.5x)</option>
            <option value="rock">Rock (∞)</option>
            <option value="forest">Forest (1.5x)</option>
            <option value="mountain">Mountain (∞)</option>
            <option value="void">Void (∞)</option>
          </select>
        </div>
        <div class="property-row">
          <span class="property-label">Passable:</span>
          <span :class="['property-value', cellPassable ? 'status-yes' : 'status-no']">
            {{ cellPassable ? 'Yes' : 'No' }}
          </span>
        </div>
        <div class="property-row">
          <span class="property-label">Movement Cost:</span>
          <span class="property-value">{{ movementCost }}</span>
        </div>
      </div>

      <!-- Connections -->
      <div class="property-section">
        <h4 class="gm-section-title">Connections (Movement)</h4>
        <div class="connections-grid">
          <!-- Top row: NW, N, NE -->
          <button
            class="gm-btn connection-btn north-west"
            :class="{ blocked: isEdgeBlocked('northWest') }"
            :disabled="!isDirectionEditable('northWest')"
            @click="toggleConnection('northWest')"
          >
            ↖ NW
          </button>
          <button
            class="gm-btn connection-btn north"
            :class="{ blocked: isEdgeBlocked('north') }"
            :disabled="!isDirectionEditable('north')"
            @click="toggleConnection('north')"
          >
            ↑ North
          </button>
          <button
            class="gm-btn connection-btn north-east"
            :class="{ blocked: isEdgeBlocked('northEast') }"
            :disabled="!isDirectionEditable('northEast')"
            @click="toggleConnection('northEast')"
          >
            ↗ NE
          </button>

          <!-- Middle row: W, (center spacer), E -->
          <button
            class="gm-btn connection-btn west"
            :class="{ blocked: isEdgeBlocked('west') }"
            :disabled="!isDirectionEditable('west')"
            @click="toggleConnection('west')"
          >
            ← West
          </button>
          <div class="connection-center">Cell</div>
          <button
            class="gm-btn connection-btn east"
            :class="{ blocked: isEdgeBlocked('east') }"
            :disabled="!isDirectionEditable('east')"
            @click="toggleConnection('east')"
          >
            → East
          </button>

          <!-- Bottom row: SW, S, SE -->
          <button
            class="gm-btn connection-btn south-west"
            :class="{ blocked: isEdgeBlocked('southWest') }"
            :disabled="!isDirectionEditable('southWest')"
            @click="toggleConnection('southWest')"
          >
            ↙ SW
          </button>
          <button
            class="gm-btn connection-btn south"
            :class="{ blocked: isEdgeBlocked('south') }"
            :disabled="!isDirectionEditable('south')"
            @click="toggleConnection('south')"
          >
            ↓ South
          </button>
          <button
            class="gm-btn connection-btn south-east"
            :class="{ blocked: isEdgeBlocked('southEast') }"
            :disabled="!isDirectionEditable('southEast')"
            @click="toggleConnection('southEast')"
          >
            ↘ SE
          </button>
        </div>
      </div>

      <!-- Spawner Info -->
      <div v-if="cellData?.spawner" class="property-section">
        <h4 class="gm-section-title">
          {{ isChestCell ? 'Chest Spawner' : 'Spawner' }}
        </h4>
        <div class="property-row">
          <span class="property-label">Type:</span>
          <span class="property-value spawner-type">
            {{ isChestCell ? 'object · chest' : cellData.spawner.type }}
          </span>
        </div>

        <!-- A chest carries a whole configuration, not just a name -->
        <ChestSpawnerEditor
          v-if="isChestCell"
          :model-value="cellData.spawner.properties"
          @update:model-value="updateChestProperties"
        />

        <div v-else-if="cellData.spawner.properties.name" class="property-row">
          <span class="property-label">Name:</span>
          <span class="property-value">{{ cellData.spawner.properties.name }}</span>
        </div>

        <button class="gm-btn gm-btn--danger remove-btn" @click="removeSpawner">Remove Spawner</button>
      </div>

      <!-- Game Object Info -->
      <div v-if="cellData?.gameObject" class="property-section">
        <h4 class="gm-section-title">Game Object</h4>
        <div class="property-row">
          <span class="property-label">Type:</span>
          <span class="property-value object-type">{{ cellData.gameObject.type }}</span>
        </div>
        <div v-if="cellData.gameObject.properties.name" class="property-row">
          <span class="property-label">Name:</span>
          <span class="property-value">{{ cellData.gameObject.properties.name }}</span>
        </div>
        <button class="gm-btn gm-btn--danger remove-btn" @click="removeGameObject">Remove Object</button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { TERRAIN_CONFIGS, DIRECTION_DELTAS, isChestSpawner } from '@/types/tabletop';
import ChestSpawnerEditor from './ChestSpawnerEditor.vue';

export default {
  name: 'PropertiesPanel',
  components: { ChestSpawnerEditor },
  computed: {
    ...mapGetters('tabletopEditor', [
      'selectedCell',
      'getCell',
      'getEdge',
      'currentMap',
      'gridConfig'
    ]),
    cellData() {
      if (!this.selectedCell) return null;
      return this.getCell(this.selectedCell.x, this.selectedCell.y, this.selectedCell.layer);
    },
    isChestCell() {
      return isChestSpawner(this.cellData?.spawner);
    },
    layerName() {
      if (!this.selectedCell || !this.currentMap) return '';
      const layer = this.currentMap.layers.find(l => l.id === this.selectedCell.layer);
      return layer ? layer.name : '';
    },
    cellTerrain: {
      get() {
        if (!this.cellData) return 'grass';
        return this.cellData.terrain || 'grass';
      },
      set(value) {
        // This will be handled by updateCellTerrain method
      }
    },
    cellPassable() {
      if (!this.cellData) return true;
      return this.cellData.passable !== false;
    },
    movementCost() {
      if (!this.cellData || !this.cellData.terrain) return '1.0';
      const terrainConfig = TERRAIN_CONFIGS[this.cellData.terrain];
      if (!terrainConfig) return '1.0';
      const cost = terrainConfig.movementCost;
      return cost === Infinity ? '∞' : cost.toFixed(2);
    }
  },
  methods: {
    ...mapActions('tabletopEditor', [
      'toggleCellConnection',
      'clearCellContent',
      'updateCell',
      'placeCellSpawner'
    ]),
    updateChestProperties(properties) {
      if (!this.selectedCell) return;
      this.placeCellSpawner({
        x: this.selectedCell.x,
        y: this.selectedCell.y,
        layer: this.selectedCell.layer,
        spawner: { type: 'object', properties }
      });
    },
    updateCellTerrain() {
      if (!this.selectedCell) return;

      const terrain = this.cellTerrain;
      const terrainConfig = TERRAIN_CONFIGS[terrain];
      const passable = terrainConfig.movementCost !== Infinity;

      this.updateCell({
        x: this.selectedCell.x,
        y: this.selectedCell.y,
        layer: this.selectedCell.layer,
        updates: {
          terrain,
          passable
        }
      });
    },
    isEdgeBlocked(direction) {
      if (!this.selectedCell || !this.currentMap) return false;

      const delta = DIRECTION_DELTAS[direction];
      if (!delta) return false;

      const toX = this.selectedCell.x + delta.dx;
      const toY = this.selectedCell.y + delta.dy;

      const edge = this.getEdge(
        this.selectedCell.x,
        this.selectedCell.y,
        toX,
        toY,
        this.selectedCell.layer
      );

      // If edge doesn't exist, it's not blocked (default open)
      if (!edge) return false;

      return edge.blocked === true;
    },
    isDirectionEditable(direction) {
      if (!this.selectedCell || !this.gridConfig || !this.currentMap) return false;

      const layer = this.selectedCell.layer;

      // Check layer is active
      const layerMeta = Array.isArray(this.currentMap.layers)
        ? this.currentMap.layers.find(l => l.id === layer)
        : null;
      if (!layerMeta || layerMeta.active === false) return false;

      const { columns, rows } = this.gridConfig;

      const delta = DIRECTION_DELTAS[direction];
      if (!delta) return false;

      const fromCell = this.getCell(this.selectedCell.x, this.selectedCell.y, layer);
      const fromPassable = !fromCell || fromCell.passable !== false;
      if (!fromPassable) return false;

      const nx = this.selectedCell.x + delta.dx;
      const ny = this.selectedCell.y + delta.dy;

      // Off-grid neighbors are not editable
      if (nx < 0 || ny < 0 || nx >= columns || ny >= rows) {
        return false;
      }

      const neighborCell = this.getCell(nx, ny, layer);
      const neighborPassable = !neighborCell || neighborCell.passable !== false;

      return neighborPassable;
    },
    toggleConnection(direction) {
      if (!this.selectedCell) return;
      this.toggleCellConnection({
        x: this.selectedCell.x,
        y: this.selectedCell.y,
        layer: this.selectedCell.layer,
        direction
      });
    },
    removeSpawner() {
      if (!this.selectedCell) return;
      this.clearCellContent({
        x: this.selectedCell.x,
        y: this.selectedCell.y,
        layer: this.selectedCell.layer
      });
    },
    removeGameObject() {
      if (!this.selectedCell) return;
      this.clearCellContent({
        x: this.selectedCell.x,
        y: this.selectedCell.y,
        layer: this.selectedCell.layer
      });
    }
  }
};
</script>

<style scoped>
.properties-panel {
  max-height: 37.5rem;
}

.properties-title {
  margin: 0 0 var(--gm-gap);
}

.properties-content {
  display: flex;
  flex-direction: column;
  gap: var(--gm-gap);
}

.property-section {
  background: var(--gm-surface-2);
  border: 1px solid var(--gm-border-dim);
  border-radius: var(--gm-radius);
  padding: var(--gm-gap);
}

.property-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.35rem 0;
  border-bottom: 1px solid var(--gm-border-dim);
}

.property-row:last-child {
  border-bottom: none;
}

.property-label {
  font-size: 0.75rem;
  color: var(--gm-text-muted);
  font-weight: 500;
}

.property-value {
  font-size: 0.75rem;
  color: var(--gm-text);
  font-weight: 600;
}

.terrain-select {
  padding: 0.25rem 0.5rem;
  color: var(--gm-accent);
  font-weight: 600;
}

.status-yes {
  color: var(--gm-success);
}

.status-no {
  color: var(--gm-danger);
}

.spawner-type {
  text-transform: capitalize;
  color: var(--gm-info);
}

.object-type {
  text-transform: capitalize;
  color: var(--gm-accent);
}

.connections-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--gm-gap-sm);
}

.connection-btn {
  font-size: 0.68rem;
}

.connection-center {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.68rem;
  color: var(--gm-text-muted);
}

.connection-btn.blocked {
  background: rgba(201, 69, 63, 0.14);
  border-color: var(--gm-danger);
  color: var(--gm-danger);
}

.remove-btn {
  width: 100%;
  margin-top: var(--gm-gap-sm);
}
</style>
